import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  finances_comment: string;
};

interface Props {
  invoiceId: number;
  onClose: () => void;
}

const InvoicesRejectModal: FC<Props> = ({ invoiceId, onClose }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>();

  const onSubmit = handleSubmit(async (data) => {
    try {
      await api.post(
        `/v1/payments/invoices/${invoiceId}/finances-reject`,
        data,
      );
      await queryClient.invalidateQueries(['invoices_list'], { exact: false });
      alerts.success('Invoice rejected successfully');
      onClose();
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts);
    }
  });

  return (
    <Modal>
      <SidePanel heading="Request Changes" onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="finances_comment"
                label="Comment"
                type="textarea"
                placeholder="Add comment"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Confirm
            </Button>
            <Button color="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default InvoicesRejectModal;
